/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */

import type * as Immutable from 'immutable';

import type ParameterBinding from 'views/logic/parameters/ParameterBinding';
import { singleton } from 'logic/singleton';

import type { ParameterBindingJsonRepresentation } from './ParameterBinding';

type InternalState = {
  type: string;
  name: string;
  title: string;
  description: string;
  dataType: string;
  defaultValue: any;
  optional: boolean;
  binding: ParameterBinding | undefined | null;
};

export type ParameterJson = {
  type: string;
  name: string;
  title: string;
  description: string;
  data_type: string;
  default_value: any;
  optional: boolean;
  binding: ParameterBindingJsonRepresentation | undefined | null;
};

type ParameterSubClass = {
  fromJSON: (json: ParameterJson) => Parameter;
};

class Parameter {
  protected _value: InternalState;

  static __registrations: { [key: string]: ParameterSubClass } = {};

  constructor(
    type: string,
    name: string,
    title: string,
    description: string,
    dataType: string,
    defaultValue: any,
    optional: boolean,
    binding?: ParameterBinding,
  ) {
    this._value = { type, name, title, description, dataType, defaultValue, optional, binding };
  }

  get type(): string {
    return this._value.type;
  }

  get name(): string {
    return this._value.name;
  }

  get title(): string {
    return this._value.title;
  }

  get description(): string {
    return this._value.description;
  }

  get dataType(): string {
    return this._value.dataType;
  }

  get defaultValue(): any {
    return this._value.defaultValue;
  }

  get optional(): boolean {
    return this._value.optional;
  }

  // screw you eslint, using param.constructor.needsBinding() is ugly
  // eslint-disable-next-line class-methods-use-this
  get needsBinding(): boolean {
    return true;
  }

  get binding(): ParameterBinding | undefined | null {
    return this._value.binding;
  }

  static fromJSON(value: ParameterJson): Parameter {
    const { type = 'value-parameter-v1' } = value; // default to ValueParameter in case type is empty
    const implementingClass = Parameter.__registrations[type.toLowerCase()];

    if (implementingClass) {
      return implementingClass.fromJSON(value);
    }

    throw new Error(`No class found for type <${type}>`);
  }

  static registerSubtype(type: string, implementingClass: ParameterSubClass) {
    this.__registrations[type.toLowerCase()] = implementingClass;
  }

  // eslint-disable-next-line class-methods-use-this
  toJSON() {
    throw new Error('Method toJSON has no implementation');
  }
}

const SingletonParameter = singleton('views.logic.parameters.Parameter', () => Parameter);
// eslint-disable-next-line @typescript-eslint/no-redeclare
type SingletonParameter = InstanceType<typeof Parameter>;

export default SingletonParameter;

export type ParameterMap = Immutable.Map<string, Parameter>;
